import React, { FC, useCallback, useContext, useMemo, useState } from 'react';
import api from 'api';
import classNames from 'classnames';
import { CollapsibleSection } from 'components/CollapsibleSection';
import useMatchMedia from 'hooks/useMatchMedia';
import ClipboardcopyIcon from 'icons/Clipboardcopy.icon';
import UserIcon from 'icons/User.icon';
import XIcon from 'icons/X.icon';
import { LeadStages } from 'models/Lead';
import { Button, Loader, Select } from 'ncoded-component-library';
import PopNotificationsContext from 'providers/PopNotifications/PopNotifications.context';
import { useTranslation } from 'react-i18next';
import { formatPhoneNumber } from 'phoneNumberUtils';
import LeadsContext from '../../../../providers/Leads/Leads.context';
import ListingContext from '../../../../providers/Listing/Listing.context';
import { LEAD_STATE } from '../../../../utils';
import TrashIcon from 'icons/Trash.icon';
import CheckcircleIcon from 'icons/Checkcircle.icon';
import XcircleIcon from 'icons/Xcircle.icon';
import {
  SEARCH_PREFERENCES_COPY_AND_KEY,
  PROPERTY_LIST_KEYS,
  IS_VERIFIED_PROP_LIST,
  REQUESTED_PRIVATE_VIEWER_COPY_AND_KEY,
} from './data';
import NDAStatusCard from '../../../NDAStatusCard';
import CurrentUserContext from 'providers/CurrentUser/CurrentUser.context';
import InformationcircleIcon from 'icons/Informationcircle.icon';
import usePastTime from 'hooks/usePastTime';
import MoveIcon from 'icons/Move.icon';
import ShowEnquiryAnswers from 'router/subrouters/Dash/subrouters/private/components/ShowEnquiryAnswers';
import KeyActions from 'router/subrouters/DataRoom/components/KeyActions';
import useCopyToClipboard from 'hooks/useCopyToClipboard';
import useCreateSelectOptions from 'hooks/useCreateSelectOptions';
import useDeleteLead from '../../../../hooks/useDeleteLead';

import './LeadInformation.styles.scss';

const generateGridContent = (
  CopiesObject: any,
  ValuesObject: any,
  t: (val: string) => string,
) => {
  let shouldShowContent = false;

  const content = Object.entries(CopiesObject).map(
    ([key, value]: [any, any]) => {
      const bqVal = ValuesObject?.[key as any];
      const isArray = Array.isArray(bqVal);
      const shouldShow = isArray ? !!bqVal.length : !!bqVal;

      if (!shouldShowContent) shouldShowContent = shouldShow;

      return (
        <div key={key}>
          <label>{t(value)}</label>
          {shouldShow ? (
            isArray ? (
              <ul>
                {(bqVal as Array<string>).map((val) => (
                  <li key={val}>
                    <p>{t(val)}</p>
                  </li>
                ))}
              </ul>
            ) : (
              <p>{t(bqVal)}</p>
            )
          ) : (
            <p>-</p>
          )}
        </div>
      );
    },
  );

  if (shouldShowContent) return content;
  else return null;
};

const LeadInformation: FC = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [isUpdatingLead, setIsUpdatingLead] = useState(false);

  const { popServerError } = useContext(PopNotificationsContext);
  const { isSuperAdmin } = useContext(CurrentUserContext);
  const {
    selectedLead: lead,
    setSelectedLead,
    updateLead,
    removeLead,
    changeState,
    closeLeadDrawer,
  } = useContext(LeadsContext);
  const {
    areActionsDisabled,
    dealRoom: { bossModeReferrerOptions },
  } = useContext(ListingContext);

  const isTouchDevice = useMatchMedia('isTouchDevice');

  const pastTime = usePastTime();

  const copyToClipboard = useCopyToClipboard();

  const { t } = useTranslation();

  const bossModeReferrer = useCreateSelectOptions()(bossModeReferrerOptions);

  const moveTo = useCallback(
    (newStage: LeadStages) => {
      changeState(lead, newStage, 0);
      setSelectedLead(null);
    },
    [changeState, lead, setSelectedLead],
  );

  const RequestedPrivateViewerInfo = useMemo(() => {
    if (!lead) return null;

    const content = generateGridContent(
      REQUESTED_PRIVATE_VIEWER_COPY_AND_KEY,
      lead,
      t,
    );

    if (!content) return null;

    return <div className="grid">{content}</div>;
  }, [lead, t]);

  const SearchPrefSection = useMemo(() => {
    if (!lead) return null;

    const content = generateGridContent(
      SEARCH_PREFERENCES_COPY_AND_KEY,
      lead.buyer,
      t,
    );

    return (
      <div style={{ width: '100%' }}>
        <p className="res-drawer-heading">{t('acquisitionPreferences')}</p>

        <div className="search-preferences-section__grid grid">
          {content || t('none')}
        </div>
      </div>
    );
  }, [lead, t]);

  const RenderAction = useCallback(
    (newStage: LeadStages, label: string) => (
      <Button
        className="lead-contact-information__button"
        variant="outline"
        icon={MoveIcon}
        disabled={areActionsDisabled}
        onClick={() => {
          if (areActionsDisabled) return;
          moveTo(newStage);
        }}
      >
        {label}
      </Button>
    ),
    [areActionsDisabled, moveTo],
  );

  const deleteLead = useDeleteLead();

  const onDelete = useCallback(
    async (e) => {
      e.stopPropagation();

      deleteLead(lead?._id, setIsLoading, () => {
        closeLeadDrawer();
        removeLead(lead?._id);
      });
    },
    [closeLeadDrawer, deleteLead, lead?._id, removeLead],
  );

  return (
    <>
      {lead ? (
        <div className="lead-information">
          {isLoading && <Loader />}

          <NDAStatusCard />

          <CollapsibleSection
            defaultOpen
            className={classNames('lead-enquiry', 'white-panel')}
            trigger={
              <p className="res-drawer-heading">
                <UserIcon />
                {t('namesEnquiry', { name: lead.firstName })}
              </p>
            }
          >
            {RequestedPrivateViewerInfo}

            <ShowEnquiryAnswers enquiryQuestions={lead?.enquiryQuestions} />

            {SearchPrefSection}

            <div className="lead-contact-information">
              <p className="res-drawer-heading">{t('contactInformation')}</p>
              <ul className="lead-contact-information__property-list">
                {PROPERTY_LIST_KEYS.map((key) => {
                  if (!lead[key]) return null;

                  const verifiedProp = IS_VERIFIED_PROP_LIST[key];

                  return (
                    <li key={key}>
                      <Button
                        onClick={(ev) => {
                          copyToClipboard(ev, lead[key]);
                        }}
                      >
                        <span>
                          <p className="info">
                            {key === 'contactNumber'
                              ? formatPhoneNumber(lead[key])
                              : lead[key]}
                          </p>
                        </span>
                        <ClipboardcopyIcon />
                        {lead.buyer?.[verifiedProp] ? (
                          <CheckcircleIcon className="verified" />
                        ) : (
                          <XcircleIcon className="not-verified" />
                        )}
                      </Button>
                    </li>
                  );
                })}
              </ul>

              {isTouchDevice && (
                <div className="lead-contact-information__action-wrapper">
                  {(lead.isRequestedPrivateViewer ||
                    lead.isPrivateViewer ||
                    lead.leadState === LEAD_STATE.ENQUIRE) &&
                    RenderAction('accept', t('moveToAccept'))}

                  {lead.leadState === LEAD_STATE.ACCEPT &&
                    RenderAction('confirm', t('moveToInterestConfirmed'))}
                </div>
              )}

              <p className="enquiry-date">
                {t('enquiredDate', { date: pastTime(lead.createdAt) })}
              </p>
            </div>
          </CollapsibleSection>

          {isSuperAdmin && (
            <CollapsibleSection
              className={classNames('lead-source-section', 'white-panel')}
              trigger={
                <p className="lead-source-section__heading">
                  <InformationcircleIcon />
                  {t('leadSource')}
                  <span>{lead.referrerName}</span>
                </p>
              }
            >
              <label>{t('pleaseSelectLeadSource')}</label>
              <Select
                options={bossModeReferrer}
                renderAsPortal
                portalClassName="lead-source-section-portal"
                value={lead.referrerName}
                onChange={async ({ value }: { value: string }) => {
                  setIsUpdatingLead(true);
                  try {
                    const { data } = await api.portfolio.seller.updateLead(
                      lead._id,
                      {
                        referrerName: value,
                      },
                    );
                    setSelectedLead(data);
                    updateLead(data);
                  } catch (e) {
                    popServerError(e);
                  } finally {
                    setIsUpdatingLead(false);
                  }
                }}
              />
            </CollapsibleSection>
          )}

          {isSuperAdmin && (
            <KeyActions description={t('deleteLeadDescription')}>
              <Button variant="solid" icon={TrashIcon} onClick={onDelete}>
                {t('deleteLead')}
              </Button>
            </KeyActions>
          )}

          {isUpdatingLead && <Loader />}

          <Button
            className="close-drawer-button"
            icon={XIcon}
            onClick={() => setSelectedLead(null)}
          />
        </div>
      ) : (
        <Loader />
      )}
    </>
  );
};

export default LeadInformation;
